import { RefObject, useEffect } from 'react';
import { OrthographicCamera, PerspectiveCamera, WebGLRenderer } from 'three';
import { throttle } from 'underscore';

import { isDefined } from '../../../utils/variables';
import { RenderItem } from './use-animate';

/**
 * Hookup the ThreeJS rendering items to the window 'resize' event to automatically resize them
 * when the window size changes.
 */
export function useWindowResize(
  items: RenderItem[],
  ref: RefObject<HTMLElement>,
  camera?: PerspectiveCamera | OrthographicCamera,
) {
  useEffect(() => {
    if (items.length === 0 || !isDefined(ref.current) || !isDefined(camera)) {
      return;
    }

    const container = ref.current;

    const onResize =
      camera instanceof PerspectiveCamera
        ? () => {
            camera.aspect = container.clientWidth / container.clientHeight;
            camera.updateProjectionMatrix();

            for (const item of items) {
              item.renderer.setSize(container.clientWidth, container.clientHeight);
            }
          }
        : () => {
            const zoomLevel = (camera.right - camera.left) / container.clientWidth;

            // Update the frustum dimensions based on the new aspect ratio and zoom level
            camera.left = (-container.clientWidth / 2) * zoomLevel;
            camera.right = (container.clientWidth / 2) * zoomLevel;
            camera.top = (container.clientHeight / 2) * zoomLevel;
            camera.bottom = (-container.clientHeight / 2) * zoomLevel;

            camera.updateProjectionMatrix();

            for (const item of items) {
              item.renderer.setSize(container.clientWidth, container.clientHeight);

              if (item.renderer instanceof WebGLRenderer) {
                item.renderer.setPixelRatio(window.devicePixelRatio);
              }
            }
          };

    const callback = throttle(onResize, 100);
    window.addEventListener('resize', callback);
    return () => {
      window.removeEventListener('resize', callback);
    };
  }, [items, ref, camera]);
}
